import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import { AccessTime, Today } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { colors } from '../../themes/typography';

/**
 * Modal for confirming early start of accepted experiments
 */
const StartEarlyModal = ({
  open,
  onClose,
  onConfirm,
  currentStartDate,
  endDate,
  loading,
  t
}) => {
  // Get current language for date formatting
  const { i18n } = useTranslation();
  
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString({ es: 'es-ES', en: 'en-US', it: 'it-IT' }[i18n.language] || 'es-ES', { 
      day: 'numeric', 
      month: 'long' 
    });
  };

  const handleConfirm = () => {
    const today = new Date();
    const startDate = new Date(currentStartDate);
    
    // Validate that experiment hasn't started yet
    if (startDate <= today) {
      return;
    }
    
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          p: '8px'
        }
      }}
    >
      <DialogTitle sx={{
        fontSize: '16px',
        fontWeight: 700,
        color: colors.text.primary,
        pb: '12px'
      }}>
        {t('startExperimentNow')}
      </DialogTitle>

      <DialogContent sx={{ pb: '16px' }}>
        {/* Current Schedule */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          mb: '12px',
          p: '8px',
          backgroundColor: colors.background.subtle,
          borderRadius: '8px'
        }}>
          <AccessTime sx={{ color: colors.text.secondary, fontSize: '16px' }} />
          <Typography sx={{
            fontSize: '14px',
            color: colors.text.primary,
            fontWeight: 500
          }}>
            {t('currentSchedule')}: {formatDate(currentStartDate)} - {formatDate(endDate)}
          </Typography>
        </Box>

        {/* New Schedule */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px', 
          mb: '16px',
          p: '8px',
          backgroundColor: 'rgba(76, 175, 80, 0.08)',
          border: `1px solid #4caf50`,
          borderRadius: '8px'
        }}>
          <Today sx={{ color: '#4caf50', fontSize: '16px' }} />
          <Typography sx={{
            fontSize: '14px',
            color: '#4caf50',
            fontWeight: 600
          }}>
            {t('newSchedule')}: {t('today')} - {formatDate(endDate)}
          </Typography>
        </Box>

        <Typography sx={{
          fontSize: '13px',
          color: colors.text.secondary,
          fontStyle: 'italic',
          textAlign: 'center'
        }}>
          {t('experimentWillStart')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ 
        px: '24px', 
        pb: '16px', 
        gap: '12px' 
      }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            color: colors.text.secondary,
            borderColor: colors.border.medium,
            '&:hover': {
              backgroundColor: colors.background.subtle
            }
          }}
          variant="outlined"
        >
          {t('cancel')}
        </Button>
        
        <Button
          onClick={handleConfirm}
          disabled={loading}
          sx={{
            backgroundColor: '#4caf50',
            color: 'white',
            '&:hover': {
              backgroundColor: '#4caf50',
              opacity: 0.9
            },
            '&:disabled': {
              opacity: 0.6
            }
          }}
          variant="contained"
        >
          {loading ? t('loading') : t('startNow')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StartEarlyModal;